import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useUser } from '../context/UserContext';
import {
  moraCobrarTmc,
  moraCrearSolicitud,
  moraHistorialTasas,
  moraListarClientes,
  moraListarDocumentos,
  moraReporteMargen,
} from '../api/apiConfig';
import { canAccessSimuladorMora, defaultHomePath } from '../utils/gestionCampanaAccess';
import './SimuladorMoraExcepcion.css';
import './SolicitudesExcepcionMora.css';

const CLP = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
const PCT = new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PCT_TMC = new Intl.NumberFormat('es-CL', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseLocalDate(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDate(iso) {
  const d = parseLocalDate(iso);
  return d ? d.toLocaleDateString('es-CL') : '—';
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('es-CL');
}

function deudorKey(d) {
  const rut = String(d?.rut_deudor_cli || '').trim().toUpperCase();
  if (rut) return rut;
  const nom = String(d?.deudor_nombre || '').trim();
  return nom ? `N:${nom.toUpperCase()}` : '__SIN__';
}

function deudorMeta(d) {
  const nombre = String(d?.deudor_nombre || '').trim();
  const rut = String(d?.rut_deudor_cli || '').trim();
  if (nombre || rut) {
    return { nombre: nombre || 'Deudor', rut };
  }
  return { nombre: 'Sin deudor', rut: '' };
}

function groupRowsByDeudor(list) {
  const map = new Map();
  for (const r of list || []) {
    const k = deudorKey(r);
    if (!map.has(k)) {
      map.set(k, { key: k, ...deudorMeta(r), rows: [] });
    }
    map.get(k).rows.push(r);
  }
  return [...map.values()].sort((a, b) => {
    if (a.key === '__SIN__') return 1;
    if (b.key === '__SIN__') return -1;
    return a.nombre.localeCompare(b.nombre, 'es');
  });
}

function monthStartISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function operacionLabel(row) {
  if (row?.num_opes_label) return String(row.num_opes_label);
  if (row?.operaciones) {
    const parts = String(row.operaciones)
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length === 1) return `Op. ${parts[0]}`;
    if (parts.length <= 3) return `Ops. ${parts.join(', ')}`;
    if (parts.length > 3) return `${parts.length} operaciones`;
  }
  return '—';
}

function daysInArrears(dueDate, paymentDate) {
  const a = parseLocalDate(dueDate);
  const b = parseLocalDate(paymentDate);
  if (!a || !b) return 0;
  return Math.max(0, Math.round((b - a) / 86400000));
}

function calculateMora(debt, monthlyRatePct, days) {
  return Math.round(Number(debt || 0) * (Number(monthlyRatePct || 0) / 100) * days / 30);
}

function getApproval(rate) {
  if (Number(rate) > 3.5) {
    return { label: 'Líder de equipo', reason: 'Tasa solicitada superior a 3,50%' };
  }
  return { label: 'Gerencia', reason: 'Tasa solicitada igual o inferior a 3,50%' };
}

const SimuladorMoraExcepcion = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (!canAccessSimuladorMora(user)) {
      navigate(defaultHomePath(user), { replace: true });
    }
  }, [user, navigate]);

  const [clientes, setClientes] = useState([]);
  const [rutSel, setRutSel] = useState('');
  const [clienteQuery, setClienteQuery] = useState('');
  const [clienteMenuOpen, setClienteMenuOpen] = useState(false);
  const [clientesHint, setClientesHint] = useState('');
  const [plazoDias, setPlazoDias] = useState(0);
  const [requestedRate, setRequestedRate] = useState('');
  const [tmc, setTmc] = useState(null);
  const [clienteInfo, setClienteInfo] = useState(null);
  const [docs, setDocs] = useState([]);
  const [excluidosAprobados, setExcluidosAprobados] = useState(0);
  const [excluidosSinPago, setExcluidosSinPago] = useState(0);
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState('');
  const [motivo, setMotivo] = useState('');
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState('');
  const [successBanner, setSuccessBanner] = useState(null);
  const [tab, setTab] = useState('simular');
  const [fechaDesde, setFechaDesde] = useState(monthStartISO());
  const [fechaHasta, setFechaHasta] = useState(todayISO());
  const [impacto, setImpacto] = useState(null);
  const [loadingImpacto, setLoadingImpacto] = useState(false);
  const [mesAcumulado, setMesAcumulado] = useState(null);
  const [loadingMesAcumulado, setLoadingMesAcumulado] = useState(false);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [historialTasas, setHistorialTasas] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(''), 3200);
  }, []);

  const loadClientes = useCallback(async () => {
    if (!user) return;
    setLoadingClientes(true);
    try {
      const res = await moraListarClientes(user, {});
      setClientes(Array.isArray(res?.clientes) ? res.clientes : []);
      if (res?.plazo_dias != null) setPlazoDias(Number(res.plazo_dias) || 0);
      setClientesHint(res?.message || '');
    } catch (e) {
      console.error(e);
      showToast(e.message || 'Error al cargar clientes');
      setClientes([]);
      setClientesHint('');
    } finally {
      setLoadingClientes(false);
    }
  }, [user, showToast]);

  useEffect(() => {
    loadClientes();
  }, [loadClientes]);

  useEffect(() => {
    if (!rutSel || !clientes.length) return;
    const c = clientes.find((x) => x.RUT_CLI === rutSel);
    if (c && !clienteMenuOpen) {
      const label = `${c.CLIENTE || c.RUT_CLI} (${c.RUT_CLI})`;
      if (clienteQuery !== label) setClienteQuery(label);
    }
  }, [clientes, rutSel, clienteMenuOpen, clienteQuery]);

  const clienteSeleccionado = useMemo(
    () => clientes.find((c) => c.RUT_CLI === rutSel) || null,
    [clientes, rutSel]
  );

  const clientesFiltrados = useMemo(() => {
    const q = clienteQuery.trim().toLowerCase().replace(/\./g, '');
    if (!q) return clientes;
    return clientes.filter((c) => {
      const nombre = String(c.CLIENTE || '').toLowerCase();
      const rut = String(c.RUT_CLI || c.rut_cli || '').toLowerCase().replace(/\./g, '');
      const ejec = String(c.EJECUTIVO || '').toLowerCase();
      const docs = String(c.N_DOCS ?? '');
      return (
        nombre.includes(q) ||
        rut.includes(q) ||
        ejec.includes(q) ||
        docs.includes(q)
      );
    });
  }, [clientes, clienteQuery]);

  const pickCliente = useCallback((c) => {
    const rut = c?.RUT_CLI || '';
    setRutSel(rut);
    setClienteQuery(c ? `${c.CLIENTE || rut} (${rut})` : '');
    setClienteMenuOpen(false);
    setSelected({});
    setMotivo('');
  }, []);

  const clearCliente = useCallback(() => {
    setRutSel('');
    setClienteQuery('');
    setClienteMenuOpen(false);
    setSelected({});
    setMotivo('');
    setDocs([]);
    setExcluidosAprobados(0);
    setClienteInfo(null);
    setTmc(null);
    setHistorialOpen(false);
    setHistorialTasas([]);
  }, []);

  const openHistorialTasas = useCallback(async () => {
    if (!user || !rutSel) {
      showToast('Selecciona un cliente primero');
      return;
    }
    setHistorialOpen(true);
    setLoadingHistorial(true);
    setHistorialTasas([]);
    try {
      const res = await moraHistorialTasas(user, { rut_cli: rutSel, limit: 15 });
      setHistorialTasas(Array.isArray(res?.historial) ? res.historial : []);
    } catch (e) {
      console.error(e);
      showToast(e.message || 'No se pudo cargar el historial de tasas');
      setHistorialTasas([]);
    } finally {
      setLoadingHistorial(false);
    }
  }, [user, rutSel, showToast]);

  const loadDocumentos = useCallback(async () => {
    if (!user || !rutSel) {
      setDocs([]);
      setExcluidosAprobados(0);
      setExcluidosSinPago(0);
      setClienteInfo(null);
      setTmc(null);
      return;
    }
    setLoadingDocs(true);
    try {
      const rateNum = requestedRate === '' ? undefined : Number(requestedRate);
      const res = await moraListarDocumentos(user, {
        rut_cli: rutSel,
        tasa_mensual_solicitada: Number.isFinite(rateNum) ? rateNum : undefined,
      });
      setClienteInfo(res?.cliente || null);
      setTmc(res?.tmc || null);
      if (res?.plazo_dias != null) setPlazoDias(Number(res.plazo_dias) || 0);
      const list = Array.isArray(res?.documentos) ? res.documentos : [];
      setDocs(list);
      setExcluidosAprobados(Number(res?.excluidos_aprobados || 0));
      setExcluidosSinPago(Number(res?.excluidos_sin_pago_confirmado || 0));
      setSelected((prev) => {
        const next = {};
        list.forEach((d) => {
          const key = String(d.id ?? d.num_docto);
          next[key] = prev[key] !== undefined ? prev[key] : true;
        });
        return next;
      });
      // No autocompletar con TMC: este módulo solo pide excepciones (tasa < TMC).
    } catch (e) {
      console.error(e);
      const msg = e.message || 'Error al cargar documentos';
      showToast(msg);
      setDocs([]);
      setExcluidosAprobados(0);
      setExcluidosSinPago(0);
      // Cliente con preacordada: no debe quedar seleccionado en el simulador
      if (String(e?.code || e?.error || msg).includes('preacordada') || /preacordada/i.test(msg)) {
        clearCliente();
      }
    } finally {
      setLoadingDocs(false);
    }
  }, [user, rutSel, requestedRate, showToast, clearCliente]);

  useEffect(() => {
    loadDocumentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recarga al cambiar cliente; tasa se recalcula en cliente
  }, [user, rutSel]);

  const tmcMonthly = tmc ? Number(tmc.porcentaje_mensual || 0) : 0;
  const rate = Number(requestedRate);
  const rateOk = Number.isFinite(rate) && rate >= 0;
  const approval = getApproval(rateOk ? rate : tmcMonthly);

  const rows = useMemo(() => {
    return docs.map((d) => {
      const key = String(d.id ?? d.num_docto);
      const fechaInicio = d.fec_inicio_mora || d.fec_vcto;
      const fechaPagoDoc = d.fecha_pago || null;
      const days =
        d.dias_mora != null
          ? Number(d.dias_mora)
          : daysInArrears(fechaInicio, fechaPagoDoc);
      const debt = Number(d.deuda_neta || 0);
      const moraTmc = calculateMora(debt, tmcMonthly, days);
      const moraReq = calculateMora(debt, rateOk ? rate : 0, days);
      return {
        ...d,
        key,
        fechaInicio,
        fechaPagoDoc,
        days,
        debt,
        moraTmc,
        moraReq,
        isSelected: Boolean(selected[key]),
      };
    });
  }, [docs, tmcMonthly, rate, rateOk, selected]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const doc = String(r.num_docto || '').toLowerCase();
      const nom = String(r.deudor_nombre || '').toLowerCase();
      const rut = String(r.rut_deudor_cli || '').toLowerCase();
      return doc.includes(q) || nom.includes(q) || rut.includes(q);
    });
  }, [rows, search]);

  const visibleGroups = useMemo(() => groupRowsByDeudor(visibleRows), [visibleRows]);
  const nDeudoresVisible = visibleGroups.filter((g) => g.rows.length).length;

  const selectedRows = useMemo(() => rows.filter((r) => r.isSelected), [rows]);

  const exportDocsExcel = () => {
    const source = visibleRows.length ? visibleRows : rows;
    if (!source.length) {
      showToast('No hay documentos para exportar');
      return;
    }
    const data = source.map((r) => ({
      Cliente: clienteInfo?.nombre || clienteInfo?.cliente || '',
      RUT: rutSel || '',
      Deudor: r.deudor_nombre || '',
      'RUT deudor': r.rut_deudor_cli || '',
      'N° Doc.': r.num_docto || '',
      'N° Op.': r.num_ope != null ? r.num_ope : '',
      'Saldo Adeudado': Number(r.debt || 0),
      Vencimiento: r.fec_vcto || '',
      'Fecha pago confirmado': r.fechaPagoDoc || r.fecha_pago || '',
      'Últ. abono': r.fec_ult_pago || '',
      'Días mora': r.days,
      'Mora TMC': Number(r.moraTmc || 0),
      'Mora solicitada': Number(r.moraReq || 0),
      Seleccionado: r.isSelected ? 'Sí' : 'No',
      'Tasa solicitada (%)': requestedRate,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Documentos mora');
    XLSX.writeFile(
      wb,
      `simulador_mora_documentos_${new Date().toISOString().split('T')[0]}.xlsx`
    );
    showToast('Excel descargado');
  };

  const loadImpacto = useCallback(async () => {
    if (!user) return;
    setLoadingImpacto(true);
    try {
      const res = await moraReporteMargen(user, {
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        solo_propio: true,
      });
      setImpacto(res);
    } catch (e) {
      console.error(e);
      showToast(e.message || 'Error al cargar el impacto de margen');
      setImpacto(null);
    } finally {
      setLoadingImpacto(false);
    }
  }, [user, fechaDesde, fechaHasta, showToast]);

  const loadMesAcumulado = useCallback(async () => {
    if (!user) return;
    setLoadingMesAcumulado(true);
    try {
      const res = await moraReporteMargen(user, {
        fecha_desde: monthStartISO(),
        fecha_hasta: todayISO(),
        solo_propio: true,
      });
      setMesAcumulado(res?.resumen || null);
    } catch (e) {
      console.error(e);
      setMesAcumulado(null);
    } finally {
      setLoadingMesAcumulado(false);
    }
  }, [user]);

  useEffect(() => {
    if (tab === 'impacto') loadImpacto();
  }, [tab, loadImpacto]);

  useEffect(() => {
    if (tab === 'simular') loadMesAcumulado();
  }, [tab, loadMesAcumulado]);

  const exportImpactoExcel = () => {
    if (!impacto) {
      showToast('Calcula el resumen antes de exportar');
      return;
    }
    const res = impacto.resumen || {};
    const porM = impacto.por_mes || [];
    const det = impacto.detalle || [];
    const wb = XLSX.utils.book_new();
    const wsRes = XLSX.utils.json_to_sheet([
      {
        'Fecha desde': fechaDesde,
        'Fecha hasta': fechaHasta,
        'Margen generado con excepciones': Number(res.total_mora_solicitada || 0),
        'Mora a TMC': Number(res.total_mora_tmc || 0),
        'Diferencia (TMC − excepciones)': Number(res.total_margen_perdido || 0),
        Solicitudes: Number(res.n_solicitudes || 0),
      },
    ]);
    XLSX.utils.book_append_sheet(wb, wsRes, 'Resumen');
    if (porM.length) {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          porM.map((r) => ({
            Año: r.anio,
            Mes: r.mes,
            Solicitudes: Number(r.n_solicitudes || 0),
            'Con excepciones': Number(r.mora_solicitada || 0),
            'Mora a TMC': Number(r.mora_tmc || 0),
            Diferencia: Number(r.margen_perdido || 0),
          }))
        ),
        'Por mes'
      );
    }
    if (det.length) {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          det.map((r) => ({
            Código: r.codigo,
            Cliente: r.cliente_nombre,
            Operación: operacionLabel(r),
            'Fecha solicitud': r.fecha_solicitud,
            'TMC (%)': Number(r.tmc_mensual),
            'Tasa (%)': Number(r.tasa_mensual_solicitada),
            'Con excepciones': Number(r.mora_solicitada || 0),
            'Mora a TMC': Number(r.mora_tmc || 0),
            Diferencia: Number(r.margen_perdido || 0),
          }))
        ),
        'Solicitudes'
      );
    }
    XLSX.writeFile(wb, `mi_impacto_mora_${fechaDesde}_${fechaHasta}.xlsx`);
    showToast('Excel descargado');
  };

  const totals = useMemo(() => {
    const debt = selectedRows.reduce((s, r) => s + r.debt, 0);
    const moraTmc = selectedRows.reduce((s, r) => s + r.moraTmc, 0);
    const moraReq = selectedRows.reduce((s, r) => s + r.moraReq, 0);
    return {
      debt,
      moraTmc,
      moraReq,
      // “Deuda neta” de negocio = saldo adeudado + interés de mora
      totalDeudaNetaTmc: debt + moraTmc,
      totalDeudaNetaSol: debt + moraReq,
      marginReduction: Math.max(0, moraTmc - moraReq),
    };
  }, [selectedRows]);

  const acumuladoMesConSim = useMemo(() => {
    const baseExcepciones = Number(mesAcumulado?.total_mora_solicitada || 0);
    const baseTmc = Number(mesAcumulado?.total_mora_tmc || 0);
    const simExcepciones = Number(totals.moraReq || 0);
    const simTmc = Number(totals.moraTmc || 0);
    const conExcepciones = baseExcepciones + simExcepciones;
    const conTmc = baseTmc + simTmc;
    return {
      conExcepciones,
      conTmc,
      margenPerdido: Math.max(0, conTmc - conExcepciones),
      nAprobadas: Number(mesAcumulado?.n_solicitudes || 0),
    };
  }, [mesAcumulado, totals.moraReq, totals.moraTmc]);

  const executiveName =
    clienteInfo?.ejecutivo ||
    user?.ejecutivo_sistema_factoring ||
    user?.nombre ||
    '—';

  const toggleRow = (key, checked) => {
    setSelected((prev) => ({ ...prev, [key]: checked }));
  };

  const selectAllVisible = (checked) => {
    setSelected((prev) => {
      const next = { ...prev };
      visibleRows.forEach((r) => {
        next[r.key] = checked;
      });
      return next;
    });
  };

  const selectDeudorGroup = (group, checked) => {
    setSelected((prev) => {
      const next = { ...prev };
      (group?.rows || []).forEach((r) => {
        next[r.key] = checked;
      });
      return next;
    });
  };

  const handleCancel = () => {
    setMotivo('');
    clearCliente();
    showToast('Simulación cancelada.');
  };

  const handleRequest = async () => {
    if (!selectedRows.length) return showToast('Selecciona al menos un documento.');
    if (!motivo.trim()) return showToast('Ingresa el motivo de la excepción.');
    if (!rateOk) return showToast('Ingresa una tasa mensual solicitada válida.');
    if (!tmc || !(tmcMonthly > 0)) {
      return showToast('No hay TMC vigente para comparar la excepción.');
    }
    // Solo excepciones: la tasa pedida debe ser estrictamente menor que la TMC.
    if (rate >= tmcMonthly - 1e-9) {
      return showToast(
        `La tasa debe ser menor que la TMC (${PCT_TMC.format(tmcMonthly)}%). Este módulo solo gestiona excepciones.`
      );
    }

    setSubmitting(true);
    try {
      const res = await moraCrearSolicitud(user, {
        rut_cli: rutSel,
        cliente_nombre: clienteInfo?.nombre,
        tasa_mensual_solicitada: rate,
        motivo: motivo.trim(),
        documentos: selectedRows.map((r) => ({
          id: r.id,
          num_docto: r.num_docto,
          num_ope: r.num_ope,
        })),
      });
      const nivel =
        res?.nivel_aprobacion === 'LIDER_EQUIPO' ? 'Líder de equipo' : 'Gerencia';
      const mailOk = Number(res?.email?.sent || 0) > 0;
      const mailEjecOk = Number(res?.email_ejecutivo?.sent || 0) > 0;
      const msg =
        res?.mensaje ||
        `Solicitud ${res?.codigo || ''} enviada. Quedó en revisión de ${nivel}.`;
      setSuccessBanner({
        tipo: 'excepcion',
        codigo: res?.codigo,
        nivel,
        mensaje: msg,
        mailAprobacion: mailOk
          ? `Notificación de aprobación enviada${res?.email?.test_mode ? ' (modo prueba)' : ''}.`
          : `No se pudo notificar al aprobador${res?.email?.error ? `: ${res.email.error}` : '.'}`,
        mailEjecutivo: mailEjecOk
          ? 'Te enviamos una copia resumida por correo.'
          : `No se pudo enviar tu copia por correo${res?.email_ejecutivo?.error ? `: ${res.email_ejecutivo.error}` : '.'}`,
      });
      showToast(`Solicitud ${res?.codigo || ''} registrada.`);
      setMotivo('');
    } catch (e) {
      console.error(e);
      setSuccessBanner(null);
      showToast(e.message || 'No se pudo crear la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCobrarTmc = async () => {
    if (!selectedRows.length) return showToast('Selecciona al menos un documento.');
    if (!tmc || !(tmcMonthly > 0)) {
      return showToast('No hay TMC vigente para confirmar el cobro.');
    }
    const ok = window.confirm(
      `¿Confirmar cobro a TMC (${PCT_TMC.format(tmcMonthly)}%) para ${selectedRows.length} documento(s)?\n\n` +
        'Se enviará a Aplicaciones para cursar con prioridad. Los documentos dejarán de estar disponibles para pedir excepción.'
    );
    if (!ok) return;

    setSubmitting(true);
    try {
      const res = await moraCobrarTmc(user, {
        rut_cli: rutSel,
        cliente_nombre: clienteInfo?.nombre,
        documentos: selectedRows.map((r) => ({
          id: r.id,
          num_docto: r.num_docto,
          num_ope: r.num_ope,
        })),
      });
      const mailOpsOk = Number(res?.email_operaciones?.sent || 0) > 0;
      const mailEjecOk = Number(res?.email_ejecutivo?.sent || 0) > 0;
      setSuccessBanner({
        tipo: 'tmc',
        codigo: res?.codigo,
        mensaje:
          res?.mensaje ||
          `Solicitud ${res?.codigo || ''} enviada a Aplicaciones para cursar a TMC.`,
        tmcLabel: PCT_TMC.format(tmcMonthly),
        mailOperaciones: mailOpsOk
          ? `Aplicaciones notificada${res?.email_operaciones?.test_mode ? ' (modo prueba)' : ''}.`
          : `No se pudo notificar a Aplicaciones${res?.email_operaciones?.error ? `: ${res.email_operaciones.error}` : '.'}`,
        mailEjecutivo: mailEjecOk
          ? 'Te enviamos una copia por correo.'
          : `No se pudo enviar tu copia${res?.email_ejecutivo?.error ? `: ${res.email_ejecutivo.error}` : '.'}`,
      });
      showToast(res?.mensaje || `Solicitud ${res?.codigo || ''} enviada a Aplicaciones.`);
      clearCliente();
      loadClientes();
    } catch (e) {
      console.error(e);
      setSuccessBanner(null);
      showToast(e.message || 'No se pudo confirmar el cobro a TMC');
    } finally {
      setSubmitting(false);
    }
  };

  const allVisibleSelected =
    visibleRows.length > 0 && visibleRows.every((r) => r.isSelected);
  const someVisibleSelected = visibleRows.some((r) => r.isSelected);

  const impactoResumen = impacto?.resumen || {};
  const impactoPorMes = impacto?.por_mes || [];
  const impactoDetalle = impacto?.detalle || [];

  return (
    <div className="mora-app exc-page">
      <header className="mora-page-header">
        <div>
          <h1>Simulador de Mora y Excepción</h1>
          <p>
            Simula una tasa especial sobre documentos en mora, o consulta el impacto de margen de
            tus excepciones ya aprobadas respecto de la TMC.
          </p>
        </div>
      </header>

      <div className="exc-tabs">
        <button
          type="button"
          className={tab === 'simular' ? 'active' : ''}
          onClick={() => setTab('simular')}
        >
          Simular excepción
        </button>
        <button
          type="button"
          className={tab === 'impacto' ? 'active' : ''}
          onClick={() => setTab('impacto')}
        >
          Mi impacto de margen
        </button>
      </div>

      {tab === 'impacto' && (
        <section className="mora-card exc-card">
          <p className="exc-reemplazo-intro">
            Resumen de tus solicitudes <strong>aprobadas</strong> en el período: margen de mora
            generado con las tasas especiales, referencia a TMC y la diferencia entre ambos.
          </p>
          <div className="exc-toolbar">
            <div className="exc-filters">
              <label>
                Desde
                <input
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                />
              </label>
              <label>
                Hasta
                <input
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                />
              </label>
            </div>
            <div className="exc-reemplazo-actions">
              <button
                type="button"
                className="mora-btn outline"
                onClick={loadImpacto}
                disabled={loadingImpacto}
              >
                {loadingImpacto ? 'Calculando…' : 'Calcular'}
              </button>
              <button
                type="button"
                className="mora-btn outline"
                onClick={exportImpactoExcel}
                disabled={!impacto || loadingImpacto}
              >
                Exportar Excel
              </button>
            </div>
          </div>

          {loadingImpacto ? (
            <div className="mora-empty">Calculando impacto de margen…</div>
          ) : (
            <>
              <div className="mora-kpis">
                <div className="mora-kpi exception">
                  <small>Margen generado con excepciones</small>
                  <strong>
                    {CLP.format(Number(impactoResumen.total_mora_solicitada || 0))}
                  </strong>
                  <span>
                    {Number(impactoResumen.n_solicitudes || 0)} solicitud
                    {Number(impactoResumen.n_solicitudes || 0) === 1 ? '' : 'es'} aprobada
                    {Number(impactoResumen.n_solicitudes || 0) === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="mora-kpi policy">
                  <small>Mora a TMC</small>
                  <strong>{CLP.format(Number(impactoResumen.total_mora_tmc || 0))}</strong>
                </div>
                <div className="mora-kpi">
                  <small>Diferencia (TMC − excepciones)</small>
                  <strong className="mora-warning">
                    {CLP.format(
                      Number(
                        impactoResumen.total_margen_perdido != null
                          ? impactoResumen.total_margen_perdido
                          : Math.max(
                              0,
                              Number(impactoResumen.total_mora_tmc || 0) -
                                Number(impactoResumen.total_mora_solicitada || 0)
                            )
                      )
                    )}
                  </strong>
                  <span>Menor generación respecto de la TMC</span>
                </div>
              </div>

              <h3 className="exc-section-title">Por mes</h3>
              <div className="mora-table-wrap">
                <table className="exc-table">
                  <thead>
                    <tr>
                      <th>Año</th>
                      <th>Mes</th>
                      <th>Solicitudes</th>
                      <th>Con excepciones</th>
                      <th>Mora a TMC</th>
                      <th>Diferencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {impactoPorMes.map((r) => (
                      <tr key={`${r.anio}-${r.mes}`}>
                        <td>{r.anio}</td>
                        <td>{r.mes}</td>
                        <td>{r.n_solicitudes}</td>
                        <td className="mora-warning">
                          {CLP.format(Number(r.mora_solicitada || 0))}
                        </td>
                        <td className="mora-positive">
                          {CLP.format(Number(r.mora_tmc || 0))}
                        </td>
                        <td className="mora-warning">
                          {CLP.format(Number(r.margen_perdido || 0))}
                        </td>
                      </tr>
                    ))}
                    {!impactoPorMes.length && (
                      <tr>
                        <td colSpan={6} className="mora-muted">
                          Sin aprobaciones en el período
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <h3 className="exc-section-title">Detalle de solicitudes aprobadas</h3>
              <div className="mora-table-wrap">
                <table className="exc-table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Cliente</th>
                      <th>Operación</th>
                      <th>Fecha</th>
                      <th>TMC</th>
                      <th>Tasa pedida</th>
                      <th>Con excepciones</th>
                      <th>Mora a TMC</th>
                      <th>Diferencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {impactoDetalle.map((r) => (
                      <tr key={r.codigo}>
                        <td>
                          <strong>{r.codigo}</strong>
                        </td>
                        <td className="exc-left">{r.cliente_nombre}</td>
                        <td className="exc-left">{operacionLabel(r)}</td>
                        <td>{formatDateTime(r.fecha_solicitud)}</td>
                        <td>{PCT_TMC.format(Number(r.tmc_mensual))}%</td>
                        <td className="mora-warning">
                          {PCT.format(Number(r.tasa_mensual_solicitada))}%
                        </td>
                        <td className="mora-warning">
                          {CLP.format(Number(r.mora_solicitada || 0))}
                        </td>
                        <td className="mora-positive">
                          {CLP.format(Number(r.mora_tmc || 0))}
                        </td>
                        <td className="mora-warning">
                          {CLP.format(Number(r.margen_perdido || 0))}
                        </td>
                      </tr>
                    ))}
                    {!impactoDetalle.length && (
                      <tr>
                        <td colSpan={9} className="mora-muted">
                          No hay solicitudes aprobadas en este rango
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}

      {tab === 'simular' && (
        <>
      {successBanner && (
        <div className="mora-success-banner" role="status">
          <div>
            <strong>
              {successBanner.tipo === 'tmc'
                ? `Cobro a TMC confirmado (${successBanner.codigo})`
                : `Solicitud ${successBanner.codigo} enviada`}
            </strong>
            <p>{successBanner.mensaje}</p>
            <ul>
              {successBanner.tipo === 'tmc' ? (
                <>
                  <li>
                    Tasa: <strong>{successBanner.tmcLabel}%</strong> (TMC vigente)
                  </li>
                  <li>{successBanner.mailOperaciones}</li>
                  <li>{successBanner.mailEjecutivo}</li>
                </>
              ) : (
                <>
                  <li>
                    Revisión: <strong>{successBanner.nivel}</strong>
                  </li>
                  <li>{successBanner.mailAprobacion}</li>
                  <li>{successBanner.mailEjecutivo}</li>
                </>
              )}
            </ul>
          </div>
          <button type="button" className="mora-link-btn" onClick={() => setSuccessBanner(null)}>
            Cerrar
          </button>
        </div>
      )}

      <section className="mora-top-card" aria-label="Datos generales de la simulación">
        <div className="mora-data-item mora-client-pick">
          <span className="mora-icon">👤</span>
          <div className="mora-grow mora-client-search">
            <label htmlFor="moraClienteSearch">Cliente con mora</label>
            <div className="mora-client-combo">
              <input
                id="moraClienteSearch"
                type="search"
                autoComplete="off"
                placeholder={
                  loadingClientes
                    ? 'Cargando clientes…'
                    : 'Buscar por nombre, RUT o ejecutivo…'
                }
                value={clienteQuery}
                disabled={loadingClientes}
                onFocus={() => setClienteMenuOpen(true)}
                onChange={(e) => {
                  setClienteQuery(e.target.value);
                  setClienteMenuOpen(true);
                  if (rutSel) {
                    setRutSel('');
                    setDocs([]);
                    setClienteInfo(null);
                    setTmc(null);
                    setSelected({});
                  }
                }}
                onBlur={() => {
                  window.setTimeout(() => setClienteMenuOpen(false), 150);
                }}
              />
              {(rutSel || clienteQuery) && (
                <button
                  type="button"
                  className="mora-client-clear"
                  title="Limpiar cliente"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={clearCliente}
                >
                  ✕
                </button>
              )}
              {clienteMenuOpen && !loadingClientes && (
                <ul className="mora-client-dropdown" role="listbox">
                  {clientesFiltrados.length === 0 ? (
                    <li className="mora-client-empty">
                      {clientes.length === 0
                        ? 'No hay clientes con mora en tu cartera'
                        : 'Sin coincidencias'}
                    </li>
                  ) : (
                    clientesFiltrados.slice(0, 80).map((c) => (
                      <li key={c.RUT_CLI}>
                        <button
                          type="button"
                          className={
                            c.RUT_CLI === rutSel ? 'is-active' : undefined
                          }
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pickCliente(c)}
                        >
                          <span className="mora-client-name">
                            {c.CLIENTE || c.RUT_CLI}
                          </span>
                          <span className="mora-client-meta">
                            {c.RUT_CLI}
                            {c.EJECUTIVO ? ` · ${c.EJECUTIVO}` : ''}
                            {c.N_DOCS != null ? ` · ${c.N_DOCS} docs` : ''}
                          </span>
                        </button>
                      </li>
                    ))
                  )}
                  {clientesFiltrados.length > 80 && (
                    <li className="mora-client-empty">
                      Mostrando 80 de {clientesFiltrados.length}. Afina la búsqueda.
                    </li>
                  )}
                </ul>
              )}
            </div>
            <span className="mora-microcopy">
              {loadingClientes
                ? 'Cargando…'
                : clienteSeleccionado
                  ? `${clienteSeleccionado.N_DOCS || 0} documentos elegibles`
                  : clientes.length
                    ? `${clientes.length} clientes disponibles`
                    : clientesHint || '0 clientes disponibles'}
            </span>
            {rutSel && (
              <button
                type="button"
                className="mora-link-btn mora-historial-btn"
                onClick={openHistorialTasas}
                disabled={loadingHistorial}
              >
                {loadingHistorial ? 'Cargando historial…' : 'Ver últimas 15 tasas de mora'}
              </button>
            )}
          </div>
        </div>
        <div className="mora-data-item">
          <span className="mora-icon">💼</span>
          <div>
            <small>Ejecutivo</small>
            <strong>{executiveName}</strong>
          </div>
        </div>
        <div className="mora-data-item">
          <span className="mora-icon">📅</span>
          <div>
            <small>Pagos confirmados</small>
            <strong>
              Plazo {plazoDias} día{plazoDias === 1 ? '' : 's'}
            </strong>
            <span className="mora-microcopy">
              Solo docs con pago en pizarra (hoy ≤ fecha_pago + N). Sin fecha estimada.
            </span>
          </div>
        </div>
        <div className="mora-data-item">
          <span className="mora-icon">%</span>
          <div>
            <small>TMC mensual vigente</small>
            <strong className="mora-positive">
              {tmc ? `${PCT_TMC.format(tmcMonthly)}%` : rutSel ? 'Sin tasa vigente' : '—'}
            </strong>
            <span className="mora-microcopy">
              {tmc
                ? `${tmc.periodo || 'CMF'} · ${formatDate(tmc.valid_from)}–${formatDate(tmc.valid_to)}`
                : rutSel
                  ? 'Debe existir TMC para esta fecha'
                  : 'Elige un cliente'}
            </span>
          </div>
        </div>
        <div className="mora-data-item">
          <span className="mora-icon">%</span>
          <div>
            <label htmlFor="requestedRate">Tasa mensual solicitada</label>
            <div className="mora-rate-input">
              <input
                id="requestedRate"
                type="number"
                step="0.001"
                min="0"
                max="100"
                value={requestedRate}
                onChange={(e) => setRequestedRate(e.target.value)}
                disabled={!rutSel}
              />
              <span>%</span>
            </div>
          </div>
        </div>
        <div className="mora-approval-box">
          <small>Requiere aprobación</small>
          <strong>{rutSel ? approval.label : '—'}</strong>
          <span>{rutSel ? approval.reason : ''}</span>
        </div>
      </section>

      {!rutSel ? (
        <div className="mora-empty">
          {clientesHint ||
            'Selecciona un cliente con pagos confirmados elegibles para comenzar la simulación.'}
        </div>
      ) : loadingDocs ? (
        <div className="mora-empty">Cargando documentos…</div>
      ) : (
        <>
          <section className="mora-main-grid">
            <div className="mora-left">
              <article className="mora-card">
                <div className="mora-card-header">
                  <div>
                    <h2>Selección de documentos con mora por cobrar</h2>
                    <span className="mora-muted">
                      {docs.length} documento{docs.length === 1 ? '' : 's'} elegible
                      {docs.length === 1 ? '' : 's'}
                      {excluidosAprobados > 0
                        ? ` · ${excluidosAprobados} con excepción pendiente/aprobada`
                        : ''}
                      {excluidosSinPago > 0
                        ? ` · ${excluidosSinPago} sin pago en ventana`
                        : ''}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                    }}
                  >
                    <input
                      type="search"
                      placeholder="Buscar por N° de documento o deudor…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="mora-search"
                    />
                    <button
                      type="button"
                      className="mora-btn outline"
                      onClick={exportDocsExcel}
                      disabled={!rows.length}
                    >
                      Exportar Excel
                    </button>
                  </div>
                </div>
                <div className="mora-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={allVisibleSelected}
                            ref={(el) => {
                              if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                            }}
                            onChange={(e) => selectAllVisible(e.target.checked)}
                            aria-label="Seleccionar todos"
                          />
                        </th>
                        <th>N° Doc.</th>
                        <th>Saldo Adeudado</th>
                        <th>Vencimiento</th>
                        <th>Fecha pago</th>
                        <th>Días mora</th>
                        <th>Mora TMC</th>
                        <th>Mora solicitada</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleGroups.map((g) => {
                        const allG = g.rows.length > 0 && g.rows.every((r) => r.isSelected);
                        const someG = g.rows.some((r) => r.isSelected);
                        return (
                          <React.Fragment key={g.key}>
                            <tr className="mora-deudor-group">
                              <td>
                                <input
                                  type="checkbox"
                                  checked={allG}
                                  ref={(el) => {
                                    if (el) el.indeterminate = someG && !allG;
                                  }}
                                  onChange={(e) => selectDeudorGroup(g, e.target.checked)}
                                  aria-label={`Seleccionar deudor ${g.nombre}`}
                                />
                              </td>
                              <td colSpan={7}>
                                <strong>{g.nombre}</strong>
                                {g.rut ? (
                                  <span className="mora-muted"> · {g.rut}</span>
                                ) : null}
                                <span className="mora-muted">
                                  {` · ${g.rows.length} doc${g.rows.length === 1 ? '' : 's'}`}
                                </span>
                              </td>
                            </tr>
                            {g.rows.map((r) => (
                              <tr key={r.key}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={r.isSelected}
                                    onChange={(e) => toggleRow(r.key, e.target.checked)}
                                  />
                                </td>
                                <td>
                                  <strong>{r.num_docto || '—'}</strong>
                                </td>
                                <td>{CLP.format(r.debt)}</td>
                                <td>{formatDate(r.fec_vcto)}</td>
                                <td
                                  title={
                                    r.fec_ult_pago
                                      ? `Últ. abono recaudaciones: ${formatDate(r.fec_ult_pago)}`
                                      : undefined
                                  }
                                >
                                  {r.fechaPagoDoc ? formatDate(r.fechaPagoDoc) : '—'}
                                </td>
                                <td>{r.days}</td>
                                <td>{CLP.format(r.moraTmc)}</td>
                                <td>{CLP.format(r.moraReq)}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                      {!visibleRows.length && (
                        <tr>
                          <td colSpan={8} className="mora-muted">
                            {excluidosAprobados > 0
                              ? `Sin documentos elegibles: ${excluidosAprobados} ya tienen excepción pendiente o aprobada.`
                              : excluidosSinPago > 0
                                ? `Sin documentos en ventana de pago confirmado (${excluidosSinPago} fuera de plazo).`
                                : 'Sin documentos con mora por cobrar (pago confirmado posterior al vencimiento)'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="mora-table-footer">
                  <strong>
                    {selectedRows.length} documento{selectedRows.length === 1 ? '' : 's'} seleccionado
                    {selectedRows.length === 1 ? '' : 's'}
                    {nDeudoresVisible
                      ? ` · ${nDeudoresVisible} deudor${nDeudoresVisible === 1 ? '' : 'es'}`
                      : ''}
                  </strong>
                  <button
                    type="button"
                    className="mora-link-btn"
                    onClick={() => setSelected({})}
                  >
                    Limpiar selección
                  </button>
                </div>
              </article>

              <article className="mora-card">
                <div className="mora-card-header compact">
                  <h2>Comparación de escenarios seleccionados</h2>
                </div>
                <table className="mora-comparison">
                  <thead>
                    <tr>
                      <th />
                      <th className="policy">Política TMC</th>
                      <th className="exc">Excepción</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th>Tasa mensual</th>
                      <td className="mora-positive">{PCT_TMC.format(tmcMonthly)}%</td>
                      <td className="mora-warning">{rateOk ? `${PCT.format(rate)}%` : '—'}</td>
                    </tr>
                    <tr>
                      <th>Mora total</th>
                      <td className="mora-positive">{CLP.format(totals.moraTmc)}</td>
                      <td className="mora-warning">{CLP.format(totals.moraReq)}</td>
                    </tr>
                    <tr>
                      <th>Total a pagar</th>
                      <td className="mora-positive">{CLP.format(totals.totalDeudaNetaTmc)}</td>
                      <td className="mora-warning">{CLP.format(totals.totalDeudaNetaSol)}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="mora-margin-reminder">
                  ⓘ <strong>Recuerda que toda rebaja afecta tu generación de margen total.</strong>
                </div>
              </article>
            </div>

            <div className="mora-right">
              <article className="mora-card">
                <div className="mora-card-header compact">
                  <h2>Resumen de regularización</h2>
                </div>
                <div className="mora-kpis">
                  <div className="mora-kpi">
                    <small>Total deuda neta seleccionada</small>
                    <strong>{CLP.format(totals.totalDeudaNetaTmc)}</strong>
                    <span>
                      Saldo Adeudado {CLP.format(totals.debt)} + mora {CLP.format(totals.moraTmc)}
                    </span>
                  </div>
                  <div className="mora-kpi policy">
                    <small>Total a TMC</small>
                    <strong>{CLP.format(totals.totalDeudaNetaTmc)}</strong>
                    <span>Mora total: {CLP.format(totals.moraTmc)}</span>
                  </div>
                  <div className="mora-kpi exception">
                    <small>Total con tasa solicitada</small>
                    <strong>{CLP.format(totals.totalDeudaNetaSol)}</strong>
                    <span>Mora total: {CLP.format(totals.moraReq)}</span>
                  </div>
                </div>
                <label className="mora-field-label" htmlFor="exceptionReason">
                  Motivo de la excepción
                </label>
                <textarea
                  id="exceptionReason"
                  rows={3}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ingresa el motivo de la excepción solicitada…"
                />
              </article>

              <article className="mora-margin-alert">
                <div className="mora-alert-header">⚠ Impacto en el margen</div>
                <div className="mora-alert-body">
                  <div>
                    <h2>Disminución en la generación de margen por tasa especial</h2>
                    <div className="mora-alert-amount">{CLP.format(totals.marginReduction)}</div>
                    <p>
                      al cobrar {rateOk ? `${PCT.format(rate)}%` : '—'} en vez de la TMC
                    </p>
                    <ul>
                      <li>
                        La mora total baja de <strong>{CLP.format(totals.moraTmc)}</strong> a{' '}
                        <strong>{CLP.format(totals.moraReq)}</strong>
                      </li>
                      <li>
                        El cliente paga <strong>{CLP.format(totals.marginReduction)} menos</strong>
                      </li>
                      <li>Toda rebaja reduce tu generación de margen total</li>
                    </ul>
                    <div className="mora-alert-month">
                      <div className="mora-alert-month-title">Tu acumulado del mes</div>
                      {loadingMesAcumulado ? (
                        <p className="mora-alert-month-loading">Calculando…</p>
                      ) : (
                        <div className="mora-alert-month-grid mora-alert-month-grid-3">
                          <div>
                            <small>Margen generado con excepciones</small>
                            <strong className="is-warn">
                              {CLP.format(acumuladoMesConSim.conExcepciones)}
                            </strong>
                          </div>
                          <div>
                            <small>Margen generado con TMC</small>
                            <strong>{CLP.format(acumuladoMesConSim.conTmc)}</strong>
                          </div>
                          <div>
                            <small>Margen perdido por excepciones</small>
                            <strong className="is-warn">
                              {CLP.format(acumuladoMesConSim.margenPerdido)}
                            </strong>
                          </div>
                        </div>
                      )}
                      <p className="mora-alert-month-note">
                        Incluye aprobadas del mes
                        {acumuladoMesConSim.nAprobadas
                          ? ` (${acumuladoMesConSim.nAprobadas})`
                          : ''}{' '}
                        + esta simulación.
                      </p>
                    </div>
                  </div>
                  <div className="mora-down-symbol">
                    ↓<span>$</span>
                  </div>
                </div>
              </article>
            </div>
          </section>

          <footer className="mora-action-bar">
            <button
              type="button"
              className="mora-btn outline"
              disabled={submitting || !selectedRows.length || !tmcMonthly}
              onClick={handleCobrarTmc}
            >
              Cobrar a TMC
            </button>
            <button
              type="button"
              className="mora-btn primary"
              disabled={submitting}
              onClick={handleRequest}
            >
              ✈ Solicitar excepción
            </button>
            <button type="button" className="mora-btn neutral" onClick={handleCancel}>
              ✕ Cancelar
            </button>
            <button type="button" className="mora-btn neutral" onClick={() => navigate(-1)}>
              Volver
            </button>
          </footer>
        </>
      )}
        </>
      )}

      <div className={`mora-toast${toast ? ' show' : ''}`} role="status">
        {toast}
      </div>

      {historialOpen && (
        <div
          className="exc-modal-backdrop"
          role="presentation"
          onClick={() => setHistorialOpen(false)}
        >
          <div
            className="exc-modal mora-historial-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="historialTasasTitle"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="exc-modal-header">
              <div>
                <h2 id="historialTasasTitle">Últimas tasas de mora</h2>
                <p>
                  {clienteSeleccionado?.CLIENTE || clienteInfo?.nombre || rutSel}
                  {rutSel ? ` · ${rutSel}` : ''}
                </p>
              </div>
              <button
                type="button"
                className="exc-close"
                aria-label="Cerrar"
                onClick={() => setHistorialOpen(false)}
              >
                ×
              </button>
            </div>
            <p className="mora-historial-intro">
              Tasas cobradas en recaudaciones de documentos de este cliente (máx. 15 más recientes).
            </p>
            {loadingHistorial ? (
              <div className="mora-empty">Cargando historial…</div>
            ) : (
              <div className="mora-table-wrap">
                <table className="exc-table">
                  <thead>
                    <tr>
                      <th>Fecha pago</th>
                      <th>Documento</th>
                      <th>Operación</th>
                      <th>Tasa mora</th>
                      <th>Interés mora</th>
                      <th>Vencimiento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historialTasas.map((h, idx) => (
                      <tr key={`${h.num_docto || ''}-${h.num_ope || ''}-${h.fecha_pago || ''}-${idx}`}>
                        <td>{formatDate(h.fecha_pago)}</td>
                        <td>{h.num_docto || '—'}</td>
                        <td>{h.num_ope != null && h.num_ope !== '' ? h.num_ope : '—'}</td>
                        <td>
                          <strong>{PCT.format(Number(h.tasa_mora || 0))}%</strong>
                        </td>
                        <td>
                          {h.int_mora != null && h.int_mora !== ''
                            ? CLP.format(Number(h.int_mora) || 0)
                            : '—'}
                        </td>
                        <td>{formatDate(h.fec_venc)}</td>
                      </tr>
                    ))}
                    {!historialTasas.length && (
                      <tr>
                        <td colSpan={6} className="mora-muted">
                          No hay tasas de mora registradas en recaudaciones para este cliente.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mora-historial-footer">
              <button
                type="button"
                className="mora-btn outline"
                onClick={() => setHistorialOpen(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimuladorMoraExcepcion;
